import { action, computed, observable } from 'mobx'
import {
  func,
  type IValidationSchema,
  required,
  validationContext,
} from 'validx'
import { FeatureToggles } from '@/features/feature-toggles'
import type { LegendServiceType } from '@/features/jobs/legends/legend-service-type'
import type { LegendPartDependencies } from '@/features/legends/editor/legend-part-dependencies'
import { LegendPartViewModelBase } from '@/features/legends/editor/LegendPartViewModelBase'
import { DataSourcesEditorViewModel } from '@/features/legends/data-sources/DataSourcesEditorViewModel'
import {
  type ErrorDescriptor,
  receiveErrors,
} from '@/features/legends/editor/analyzer-errors'
import { validateWhileEditing } from '@/features/legends/editor/validation'
import { asNumber } from '@/utils/number-util'
import { validateObject, when } from '@/utils/validx-util'
import { DeadlineConfigEditorViewModel } from './DeadlineConfigEditorViewModel'
import {
  createPersonalOrganizerConfig,
  createSpecsConfig,
  createTaxPlanConfig,
  createTitleConfig,
} from './FormulaConfigEditorViewModel'
import type { LegendOptions, MultiJobFlowConfig } from './legend-options'
import { MessageConfigEditorViewModel } from './MessageConfigEditorViewModel'
import { PendingConfigEditorViewModel } from './PendingConfigEditorViewModel'
import { PrepAndReviewConfigEditorViewModel } from './PrepAndReviewConfigEditorViewModel'
import { PurgeConfigEditorViewModel } from './PurgeConfigEditorViewModel'
import { RatingConfigEditorViewModel } from './RatingConfigEditorViewModel'
import { StalenessConfigEditorViewModel } from './StalenessConfigEditorViewModel'
import { TimeConfigEditorViewModel } from './TimeConfigEditorViewModel'
import { TxfConfigEditorViewModel } from './TxfConfigEditorViewModel'

/**
 * The legend's general options and option groups.
 */
export class LegendOptionsViewModel extends LegendPartViewModelBase {
  private readonly _schema: IValidationSchema<LegendOptionsViewModel> = {
    name: [required()],
    serviceType: [
      func(({ value }) => value != null, 'Service type is required.'),
    ],
    timeConfig: [validateObject('Time configuration is invalid.')],
    deadlineConfig: [validateObject('Deadline configuration is invalid.')],
    titleConfig: [validateObject('Title configuration is invalid.')],
    txfConfig: [
      when(
        () => this.isTxfEnabled,
        validateObject('Txf configuration is invalid.'),
      ),
    ],
    pendingConfig: [validateObject('Pending configuration is invalid.')],
    taxPlanConfig: [
      when(
        () => this.isTaxPlanEnabled,
        validateObject('Tax Plan configuration is invalid.'),
      ),
    ],
    personalOrganizerConfig: [
      validateObject('Personal Organizer configuration is invalid.'),
    ],
    specsConfig: [validateObject('Specs configuration is invalid.')],
    staleness: [validateObject('Staleness configuration is invalid.')],
    purge: [validateObject('Purge configuration is invalid.')],
    prepAndReviewConfig: [
      validateObject('Prep and Review configuration is invalid.'),
    ],
    dataSources: [validateObject('Data sources configuration is invalid.')],
  }

  readonly messageConfig: MessageConfigEditorViewModel
  readonly ratingConfig: RatingConfigEditorViewModel
  readonly timeConfig: TimeConfigEditorViewModel
  readonly deadlineConfig: DeadlineConfigEditorViewModel
  readonly titleConfig: ReturnType<typeof createTitleConfig>
  readonly txfConfig: TxfConfigEditorViewModel
  readonly pendingConfig: PendingConfigEditorViewModel
  readonly taxPlanConfig: ReturnType<typeof createTaxPlanConfig>
  readonly personalOrganizerConfig: ReturnType<
    typeof createPersonalOrganizerConfig
  >
  readonly specsConfig: ReturnType<typeof createSpecsConfig>
  readonly staleness: StalenessConfigEditorViewModel
  readonly purge: PurgeConfigEditorViewModel
  readonly prepAndReviewConfig: PrepAndReviewConfigEditorViewModel
  readonly dataSources: DataSourcesEditorViewModel

  /**
   * The service types to pick from, sorted.
   */
  readonly serviceTypes: [LegendServiceType | null][] = [
    ['Bookkeeping'],
    ['Consultation'],
    ['Misc'],
    ['Tax'],
  ]

  @observable accessor name = ''

  /**
   * Creates follow-up jobs based on a single select question's answer.
   * Neither builder has an editor for it; it is kept so that saving doesn't
   * drop it.
   */
  @observable.ref private accessor _multiJobFlowConfig:
    MultiJobFlowConfig | undefined = undefined
  @observable accessor description = ''
  @observable accessor imageUrl = ''
  @observable accessor serviceType: LegendServiceType | null = null
  @observable accessor sendNotificationsOnSubmit = false

  /**
   * Seconds.
   */
  @observable accessor delayNotification = 0

  @observable accessor viewOrder = 0
  @observable accessor skipBilling = false
  @observable accessor alwaysEnableVoiceCall = false
  @observable accessor targetRoles: string[] = []
  @observable accessor allowProSelection = false
  @observable accessor showAvailabilityCount = false

  readonly validation = validationContext(this, this._schema)

  constructor(
    dependencies: LegendPartDependencies,
    private readonly _getPublishedServiceType: () => LegendServiceType | null,
  ) {
    super(dependencies)
    this.ratingConfig = new RatingConfigEditorViewModel(dependencies)
    this.timeConfig = new TimeConfigEditorViewModel(dependencies)
    this.deadlineConfig = new DeadlineConfigEditorViewModel(dependencies)
    this.titleConfig = createTitleConfig(dependencies)
    this.txfConfig = new TxfConfigEditorViewModel(
      dependencies,
      () => this.isTxfEnabled,
    )
    this.pendingConfig = new PendingConfigEditorViewModel(dependencies)
    this.taxPlanConfig = createTaxPlanConfig(
      dependencies,
      () => this.isTaxPlanEnabled,
    )
    this.personalOrganizerConfig = createPersonalOrganizerConfig(
      dependencies,
      () => this.serviceType === 'Tax',
    )
    this.specsConfig = createSpecsConfig(dependencies)
    this.staleness = new StalenessConfigEditorViewModel(dependencies)
    this.purge = new PurgeConfigEditorViewModel(dependencies)
    this.dataSources = new DataSourcesEditorViewModel(dependencies)
    this.prepAndReviewConfig = new PrepAndReviewConfigEditorViewModel(
      dependencies,
    )
    this.messageConfig = new MessageConfigEditorViewModel(dependencies)
    for (const config of [
      this.ratingConfig,
      this.timeConfig,
      this.deadlineConfig,
      this.titleConfig,
      this.txfConfig,
      this.pendingConfig,
      this.taxPlanConfig,
      this.personalOrganizerConfig,
      this.specsConfig,
      this.staleness,
      this.purge,
      this.dataSources,
      this.prepAndReviewConfig,
      this.messageConfig,
    ]) {
      this.lifecycle.manage(config)
    }
    this.lifecycle.manage(validateWhileEditing(this, this._schema))
  }

  /**
   * The roles the legend can be limited to, as options to pick from.
   */
  @computed
  get availableRoles() {
    return this.dependencies.catalog.roles.map((r) => ({
      id: r.id,
      text: r.name,
    }))
  }

  /**
   * Whether the TXF integration's config shows. Hidden, it's still saved,
   * but not validated.
   */
  @computed
  get isTxfEnabled() {
    return FeatureToggles.txfIntegration()
  }

  /**
   * Whether the Tax Plan integration's config shows. Hidden, it's still
   * saved, but not validated.
   */
  @computed
  get isTaxPlanEnabled() {
    return FeatureToggles.taxPlanIntegration()
  }

  /**
   * The configs that don't show, as the legend document names them.
   */
  @computed
  get hiddenConfigFields(): (keyof LegendOptions)[] {
    const fields: (keyof LegendOptions)[] = []
    if (!this.isTxfEnabled) {
      fields.push('txfConfig')
    }
    if (!this.isTaxPlanEnabled) {
      fields.push('taxPlanConfig')
    }
    return fields
  }

  /**
   * The service type can only be picked until a version with one is
   * published.
   */
  @computed
  get canChangeServiceType() {
    return this._getPublishedServiceType() === null
  }

  @computed
  private get _json(): LegendOptions {
    const multiJobFlow = this._multiJobFlowConfig
    return {
      name: this.name,
      description: this.description,
      imageUrl: this.imageUrl,
      serviceType: this.serviceType ?? undefined,
      sendNotificationsOnSubmit: this.sendNotificationsOnSubmit,
      delayNotification: asNumber(this.delayNotification),
      viewOrder: asNumber(this.viewOrder),
      skipBilling: this.skipBilling,
      alwaysEnableVoiceCall: this.alwaysEnableVoiceCall,
      messageConfig: this.messageConfig.toJSON(),
      ratingConfig: this.ratingConfig.toJSON(),
      timeConfig: this.timeConfig.toJSON(),
      targetRoles: this.targetRoles.slice(),
      deadlineConfig: this.deadlineConfig.toJSON(),
      titleConfig: this.titleConfig.toJSON(),
      txfConfig: this.txfConfig.toJSON(),
      pendingConfig: this.pendingConfig.toJSON(),
      taxPlanConfig: this.taxPlanConfig.toJSON(),
      personalOrganizerConfig: this.personalOrganizerConfig.toJSON(),
      specsConfig: this.specsConfig.toJSON(),
      staleness: this.staleness.toJSON(),
      purge: this.purge.toJSON(),
      multiJobFlowConfig: {
        enabled: multiJobFlow?.enabled ?? false,
        questionId: multiJobFlow?.questionId ?? '',
        triggerAnswer: multiJobFlow?.triggerAnswer ?? '',
        finalAnswer: multiJobFlow?.finalAnswer ?? '',
        previousPro: multiJobFlow?.previousPro ?? false,
      },
      prepAndReviewConfig: this.prepAndReviewConfig.toJSON(),
      dataSources: this.dataSources.toJSON(),
      allowProSelection: this.allowProSelection,
      showAvailabilityCount: this.showAvailabilityCount,
      excludeLegacyYears: true,
      excludeLegacyStates: true,
    }
  }

  /**
   * Whether workers are notified when requests are submitted.
   */
  @action.bound
  setSendNotificationsOnSubmit(value: boolean) {
    this.sendNotificationsOnSubmit = value
  }

  /**
   * Sets how long, in seconds, the submit notification waits.
   */
  @action.bound
  setDelayNotification(value: number) {
    this.delayNotification = value
  }

  /**
   * Whether jobs are charged.
   */
  @action.bound
  setSkipBilling(value: boolean) {
    this.skipBilling = value
  }

  /**
   * Whether the call card shows without a purchased consultation.
   */
  @action.bound
  setAlwaysEnableVoiceCall(value: boolean) {
    this.alwaysEnableVoiceCall = value
  }

  /**
   * Limits the legend to the roles. No roles means everyone.
   */
  @action.bound
  setTargetRoles(roleIds: string[]) {
    this.targetRoles = roleIds
  }

  /**
   * Whether clients can pick a previous pro during onboarding.
   */
  @action.bound
  setAllowProSelection(value: boolean) {
    this.allowProSelection = value
  }

  /**
   * Whether clients see how many pros match before submitting.
   */
  @action.bound
  setShowAvailabilityCount(value: boolean) {
    this.showAvailabilityCount = value
  }

  /**
   * Sets the options from the legend document.
   */
  @action
  hydrate(options: LegendOptions) {
    this.messageConfig.hydrate(options.messageConfig)
    this.ratingConfig.hydrate(options.ratingConfig)
    this.deadlineConfig.hydrate(options.deadlineConfig)
    this.titleConfig.hydrate(options.titleConfig)
    this.txfConfig.hydrate(options.txfConfig)
    this.pendingConfig.hydrate(options.pendingConfig)
    this.taxPlanConfig.hydrate(options.taxPlanConfig)
    this.personalOrganizerConfig.hydrate(options.personalOrganizerConfig)
    this.specsConfig.hydrate(options.specsConfig)
    this.timeConfig.hydrate(options.timeConfig)
    this.staleness.hydrate(options.staleness)
    this.purge.hydrate(options.purge)
    this.dataSources.hydrate(options.dataSources)
    this._multiJobFlowConfig = options.multiJobFlowConfig
    this.prepAndReviewConfig.hydrate(options.prepAndReviewConfig)
    this.serviceType = options.serviceType ?? null
    this.name = options.name ?? ''
    this.description = options.description ?? ''
    this.imageUrl = options.imageUrl ?? ''
    this.sendNotificationsOnSubmit = !!options.sendNotificationsOnSubmit
    this.delayNotification = asNumber(options.delayNotification)
    this.viewOrder = asNumber(options.viewOrder)
    this.skipBilling = !!options.skipBilling
    this.alwaysEnableVoiceCall = !!options.alwaysEnableVoiceCall
    this.targetRoles = options.targetRoles ?? []
    this.allowProSelection = !!options.allowProSelection
    this.showAvailabilityCount = !!options.showAvailabilityCount
  }

  /**
   * Shows the analyzer's errors on the options.
   */
  receiveErrors(descriptor: ErrorDescriptor) {
    receiveErrors(this, descriptor.inner)
  }

  /**
   * The options as the legend document has them.
   */
  toJSON() {
    return this._json
  }
}
